import React from 'react';
import { useTranslation } from 'react-i18next';

import { Ability, dayjs, MultiSelect, ShortUser } from '@umanforge/common';

import Tabs from '@common_components/buttons/Tabs.component';
import { BookIcon } from '@common_components/icons/book.icon';
import InputText from '@common_components/inputs/textInput.component';
import BaseModal from '../../modal/components/base_modal.component';
import ActionForm from './actionForm.component';
import { useTaskModalInfo } from '../hook/taskModalInfo.hook';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';
import Checkbox from '@common_components/inputs/checkbox.component';
import { Tooltip } from 'react-tooltip';
import { SkillRT } from 'interfaces/roleTaskSkill.interface';
import RoleTaskTileForm from 'features/role_task/components/roleTaskTileForm.component';
import RecurrenceModal from './recurrenceModal.component';
import SelectInput, { GroupedOption } from '@common_components/inputs/selects/selectInput.component';

interface TaskModalInfoProps {
  onClose: () => void;
  taskId: string;
  sectorId: string;
  onActionUpdate: () => void;
}

const TaskModalInfo: React.FC<TaskModalInfoProps> = ( {
  onClose,
  taskId,
  sectorId,
  onActionUpdate
} ) => {
  const { t } = useTranslation();

  const {
    selectedLocation,
    selectedTaskAction,
    selectedEquipments,
    dateStart,
    dateEnd,
    roleTasks,
    activeTab, setActiveTab,
    task,
    pages,
    isAdmin,
    fullDay,
    recurrence
  } = useTaskModalInfo(
    taskId,
    sectorId,
  );

  const allowedSkills: SkillRT[] = [];
  const allowedAbilities: Ability[] = [];
  const sectorUsers: ShortUser[] = [];

  // Render
  return (
    <BaseModal
      isOpen={true}
      onClose={onClose}
      icon={<BookIcon className="bg-blue-200 p-2 rounded-md" size={10} />}
      title={t( 'translation:task_information' )}
      noConfirmationModal={true}
    >
      <div className="py-5 px-7 gap-y-4 bg-white rounded-md shadow-md font-texte flex-col flex">
        <Tabs items={pages} activeItem={activeTab} onItemClick={setActiveTab} />
        <div className="relative">
          <div className={'py-5 px-7 gap-y-4 h-fit  bg-white flex-col rounded-b-xl border font-texte flex'}>
            { task && (
              <div className={ activeTab === 0 ? ' visible' : 'hidden'}>
                <ActionForm
                  task={task}
                  users={roleTasks.map( ( rt: { users: ShortUser[]; } ) => { return rt.users; } ).flat()}
                  onChange={onActionUpdate}
                  isAdmin={isAdmin}
                  showValidateAllButton={true}
                  sectorId={sectorId}
                  key={ roleTasks.map( rt => { return rt.id; } ).join( '_' ) }
                />
              </div>
            ) }

            <div className={ activeTab === 1 ? '' : 'hidden' }>
              <div>
                { roleTasks.map( ( roleTask, index ) => {
                  return ( ( roleTask && sectorUsers ) ?
                    <div key={roleTask.id + index}>
                      <RoleTaskTileForm
                        isAdmin={isAdmin}
                        onChange={ () => { return; } }
                        onDelete={ () => { return; } }
                        roleTask={ roleTask }
                        index={index}
                        onILUOUpdate={ ( skillId: string, iluo: number ) => { return iluo; } }
                        skills={allowedSkills}
                        abilities={allowedAbilities}
                        sectorUsers={sectorUsers}
                        fixed={true}
                      />
                    </div>
                    : null
                  );
                } ) }
              </div>
            </div>

            { activeTab === 2 &&
              <>
                <SelectInput
                  className='w-full'
                  labelPosition='left'
                  placeholder={t( 'translation:location' )}
                  label={t( 'translation:location' )}
                  onSelect={() => { /* */ } }
                  selected={selectedLocation?.id }
                  isDisable
                  fetchOptions={ (): Promise<GroupedOption[]> => { return Promise.resolve( [ { options: [ { label: selectedLocation?.name ?? '', id: selectedLocation?.id ?? '' } ] } ] ); } }/>
                <p className="text-red-500 h-6 -mt-4">{''}</p>

                <SelectInput
                  className='w-full'
                  labelPosition='left'
                  placeholder={t( 'translation:task' )}
                  label={t( 'translation:task' )}
                  onSelect={() => { /* */ } }
                  selected={selectedTaskAction?.id }
                  isDisable
                  fetchOptions={ (): Promise<GroupedOption[]> => { return Promise.resolve( [ { options: [ { label: selectedTaskAction?.name ?? '', id: selectedTaskAction?.id ?? '' } ] } ] ); } }/>
                <p className="text-red-500 h-6 -mt-4">{''}</p>



                <div className='flex space-x-3'>
                  <div className={ `w-4/5 ${ fullDay ? 'flex space-x-5' : 'flex-col' }` }>
                    <div className={ `${ fullDay ? 'flex-col w-1/2 gap-x-5' : 'flex w-full' }` }>
                      <label className={'text-gray-600 w-4/12 mr-4 left mt-4'}>
                        { t( 'translation:beginning_of_task' ) }
                      </label>
                      <div className='flex gap-x-2 my-2 w-full'>
                        <div className='w-full'>
                          <DatePickerSoloInput
                            label={ undefined }
                            defaultValue={ dateStart }
                            onChange={ ()=>{ return; } }
                            textSize={'w-3/4'}
                            disable
                          />
                        </div>
                        <div className={`w-fit ${ fullDay && 'hidden' }`}>
                          <InputText
                            labelPosition='left'
                            value={ dateStart?.format( 'HH:mm' ) }
                            disable
                          />
                        </div>
                      </div>
                    </div>

                    <div className={ `${ fullDay ? 'flex-col w-1/2 gap-x-5' : 'flex w-full' }` }>
                      <label className={'text-gray-600 w-4/12 mr-4 left mt-4'}>
                        { t( 'translation:end_of_task' ) }
                      </label>
                      <div className='flex gap-x-2 my-2 w-full'>
                        <div className='w-full'>
                          <DatePickerSoloInput
                            label={ undefined }
                            defaultValue={ dateEnd }
                            onChange={ ()=>{ return; } }
                            textSize={'w-3/4'}
                            disable
                          />
                        </div>
                        <div className={`w-fit ${ fullDay && 'hidden' }`}>
                          <InputText
                            labelPosition='left'
                            value={ dateEnd?.format( 'HH:mm' ) }
                            disable
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='flex flex-col w-1/5'>
                    <label className={'text-gray-600 w-full flex-center mr-4 left'}>
                      { t( 'translation:full_day' ) }
                    </label>
                    <div className='w-full h-full flex items-center'>
                      <div className='w-full px-3' id={'fullDay'}>
                        <Checkbox
                          onChange={ () => { return; } }
                          defaultChecked={ fullDay }
                          label={ '' }
                          className='w-full flex-center'
                          labelClassName='w-fit'
                          key={ fullDay ? '-' : '' }
                          disabled
                        />
                        <Tooltip
                          anchorSelect={ '#fullDay' }
                          className='z-[1000] max-w-[600px] text-wrap'
                          place="top"
                          positionStrategy='fixed'
                        >
                          <span>
                            <span className='text-[14px] font-semibold underline underline-offset-2'> { t( 'translation:full_day' ) } </span>
                            <p className='pl-6 pt-1'> { t( 'translation:is_the_task_on_the_day' ) } </p>
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    <p className="h-6 -mt-1"/>
                  </div>
                </div>

                <p className="text-red-500 h-6 -mt-4">{''}</p>

                <div className={'flex mt-0'}>
                  <label className={'text-gray-600 w-3/12 mr-4 left mt-2.5'}>
                    { t( 'common:equipments' ) + ' (' + selectedEquipments.length + ')' }
                  </label>
                  <MultiSelect
                    label={ undefined }
                    disabled={ true }
                    placeholder={ t( 'translation:no_associated_equipments' ) }
                    clearable={ false }
                    selectedValues={ selectedEquipments }
                    getValues={ selectedEquipments }
                  />
                </div>

                <div className={ `${ recurrence != null ? '' : 'hidden'}` } >
                  <RecurrenceModal
                    onSave={ () => { /* */ }}
                    sectorId={ sectorId }
                    taskId={ taskId }
                    recurrence={ recurrence }
                    taskDateStart={ dateStart ?? dayjs() }
                    isOpen={ recurrence != null }
                    disable
                    onRecurrenceOpen={ () => { /** */ } }
                  />
                </div>
              </>
            }
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default TaskModalInfo;
